import React, { createContext, useContext, useEffect, useState } from "react";

type SetHeaderActions = (node: React.ReactNode) => void;

// Split into two contexts (read vs. write) rather than one combined value:
// React re-renders every `useContext` subscriber whenever the *provided
// value's identity* changes, regardless of which field a subscriber reads.
// With a single combined context, a page calling the writer hook would get
// re-rendered on every unrelated header update; if that page doesn't
// independently memoize the node it registers, the re-render manufactures a
// new node identity, which re-fires the writer's effect, which updates
// state again — an infinite render loop (reproduced by a non-memoized
// consumer in `PageHeaderActions.test.tsx`, which OOM'd before this split).
// `setHeaderActions` from `useState` is referentially stable across
// renders, so the writer context's value never changes identity and writers
// are never force-rerendered by someone else's header update.
const PageHeaderActionsContext = createContext<React.ReactNode>(null);
const SetPageHeaderActionsContext = createContext<SetHeaderActions | null>(null);

/**
 * Wraps the app shell (see `AppLayout`) so any route/page can render its own
 * controls into the shared page-title band next to "My View" (CTX-112)
 * instead of the header hard-coding a per-route switch, or a page reaching
 * for DOM queries / absolute positioning to inject itself into someone
 * else's header.
 */
export const PageHeaderActionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
	return (
		<SetPageHeaderActionsContext.Provider value={setHeaderActions}>
			<PageHeaderActionsContext.Provider value={headerActions}>{children}</PageHeaderActionsContext.Provider>
		</SetPageHeaderActionsContext.Provider>
	);
};

/** Consumed by `SideNav` to render whatever the active route registered. Renders nothing outside a provider or when no route has registered anything. */
export function usePageHeaderActionsSlot(): React.ReactNode {
	return useContext(PageHeaderActionsContext);
}

/**
 * Registers `node` as the current route's page-header content. Re-registers
 * whenever `node` changes identity, and always clears itself on unmount —
 * navigating away from a page (or the page's own state changing to `null`)
 * can never leave a stale header behind for the next route.
 *
 * No-ops gracefully outside a `PageHeaderActionsProvider` (e.g. a component
 * unit-tested in isolation without the app shell).
 */
export function usePageHeaderActions(node: React.ReactNode): void {
	const setHeaderActions = useContext(SetPageHeaderActionsContext);
	useEffect(() => {
		if (!setHeaderActions) return;
		setHeaderActions(node);
		return () => setHeaderActions(null);
	}, [setHeaderActions, node]);
}
